//dependencies
import { format } from "date-fns";
import React, { useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { faCircleXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// components
import { Avatar } from "@performyard/py-components";
import { Spinner } from "@performyard/py-components";

import VerticalExpander from "../../../../../../../components/AnimationWrappers/VerticalExpander";
import Button from "../../../../../../../components/Button";
import Link from "../../../../../../../components/Link";
import Spacer from "../../../../../../../components/Spacer";
import Typography from "../../../../../../../components/Text";

import { useGoalHistory, useMutateDeleteGoalHistory, useRbacObjectPermissions } from "../../../../../../../api/hooks";
//hooks
import useWindowSize from "../../../../../../../hooks/useWindowSize";

// services
import { GoalHistory } from "../../../../../../../api/services";

// utils
import toEmployeeUrl from "../../../../../../../utils/URLs/Employee";
import { parseGoalPermissionsForUser } from "../../../../utils";

// styles
import css from "../../ActivityStreamPanel.module.scss";

type HistoryMessageEntryProps = {
	entry: GoalHistory;
	onSelect?: () => void;
	showAsOf?: boolean;
};

const defaultClosedHeight = 644;

const HistoryMessageEntry = ({ entry, showAsOf }: HistoryMessageEntryProps) => {
	const { goal_id: goalId } = useParams<{ goal_id: string }>();
	const formattedDate = showAsOf
		? format(entry.as_of, "EEE, MMM d yyyy")
		: entry.created_at
			? format(entry.created_at, "EEE, MMM d yyyy")
			: null;
	const [isExpanded, setIsExpanded] = useState(false);
	const [, windowHeight = 100] = useWindowSize();
	const ref = useRef<HTMLDivElement>(null);
	const entryRef = useRef<HTMLDivElement>(null);

	//rounded down windowHeight*0.6 to the nearest whole number that's divisible by the line height of 16
	const roundedWindowHeightOverride = Math.floor((windowHeight * 0.6) / 16) * 16;
	const closedHeight = windowHeight > defaultClosedHeight ? defaultClosedHeight : roundedWindowHeightOverride;
	const height = ref?.current?.clientHeight || 0;

	const { data: permissions } = useRbacObjectPermissions("goal", goalId || "");

	const { userCanDeleteComment } = parseGoalPermissionsForUser(permissions);

	const { refetch: refetchActivity } = useGoalHistory({
		goalID: entry?.strategy || "",
		sortDirection: "desc",
		sortName: "_id",
	});

	const { refetch: refetchProgress } = useGoalHistory({
		goalID: entry?.strategy || "",
		sortDirection: "desc",
		sortName: "as_of",
		type: "progress",
	});

	const { mutate, isPending } = useMutateDeleteGoalHistory({
		onSuccess: () => {
			refetchActivity();
			refetchProgress();
		},
	});

	return (
		<div ref={entryRef} key={entry._id} className={css.entry}>
			<div className={css.message}>
				<Typography weight="regular" size="caption">
					{formattedDate}
				</Typography>
				<Spacer height={1} />
				<div className={css.employeeRow}>
					<Avatar
						id={entry?.actor?._id}
						size={24}
						dangerouslySetClassName={css.avatar}
						alt={entry?.actor?.name ? `${entry.actor.name}'s avatar` : "User avatar"}
					/>
					<Link href={toEmployeeUrl(entry?.actor?._id)} className={css.employeeLink} forceHardReload={true}>
						<Typography element="span" weight="semi-bold" size="paragraph-3" color="neutral-9">
							{entry?.actor?.name}{" "}
						</Typography>
					</Link>
				</div>
				<Spacer height={1} />
				<VerticalExpander isExpanded={isExpanded} closedHeight={closedHeight}>
					<div ref={ref}>
						<Typography element="span" weight="regular" size="paragraph-3">
							{entry?.message}
						</Typography>
						{userCanDeleteComment ? (
							<Typography element="span" weight="regular" size="paragraph-3">
								<button
									onClick={() =>
										mutate({
											goalID: entry?.strategy || "",
											goalHistoryID: entry?._id,
										})
									}
									className={css.iconButton}
								>
									{isPending ? <Spinner size={12} /> : <FontAwesomeIcon icon={faCircleXmark} />}
								</button>
							</Typography>
						) : null}
					</div>
				</VerticalExpander>
				{height > closedHeight ? (
					<div>
						<Spacer height={1} />
						<Button onClick={() => setIsExpanded(!isExpanded)} isHollow={true} size="micro">
							{isExpanded ? "Read Less" : "Read More"}
						</Button>
					</div>
				) : null}
			</div>
		</div>
	);
};

export default HistoryMessageEntry;
